import * as Xstate from "@xstate/react";
import classNames from "classnames";
import React, { FC, ReactNode, useEffect, useMemo } from "react";
import { IconType } from "react-icons";
import {
  LuActivity,
  LuAlertTriangle,
  LuArrowLeftRight,
  LuBanknote,
  LuCalendar,
  LuCalendarClock,
  LuCheck,
  LuClipboardList,
  LuDollarSign,
  LuGauge,
  LuHourglass,
  LuLock,
  LuMessageSquare,
  LuMoon,
  LuPenTool,
  LuPieChart,
  LuReceipt,
  LuTimer,
  LuUserCheck,
  LuUsers,
} from "react-icons/lu";
import { Link, Navigate, Route, Routes, useParams } from "react-router-dom";

import { GlobalContext } from "../../app/stateMachines/GlobalContext";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import { useAppDispatch } from "../../redux/redux";
import { getUser } from "../../services/AppService";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetCustomReportQuery } from "../../slices/CustomReportsSlice";
import { useAiStatus } from "../ai/useAiStatus";
import { CreateReportBox, MyReports } from "./custom/CreateReportBox";
import { CustomReportPage } from "./custom/CustomReportPage";
import { AgingReport } from "./reports/AgingReport";
import { BudgetReport } from "./reports/BudgetReport";
import { ByOwnerReport } from "./reports/ByOwnerReport";
import { ClientRequestsReport } from "./reports/ClientRequestsReport";
import { CompletedReport } from "./reports/CompletedReport";
import { DeadlinesReport } from "./reports/DeadlinesReport";
import { ForecastReport } from "./reports/ForecastReport";
import { PaymentsReport } from "./reports/PaymentsReport";
import { PortalReport } from "./reports/PortalReport";
import { ProfitabilityReport } from "./reports/ProfitabilityReport";
import { QuietClientsReport } from "./reports/QuietClientsReport";
import { RevenueReport } from "./reports/RevenueReport";
import { ThroughputReport } from "./reports/ThroughputReport";
import { TimeInStatusReport } from "./reports/TimeInStatusReport";
import { TimeReport } from "./reports/TimeReport";
import { UnbilledReport } from "./reports/UnbilledReport";
import { UtilizationReport } from "./reports/UtilizationReport";
import { WaitingReport } from "./reports/WaitingReport";
import { WorkloadReport } from "./reports/WorkloadReport";
import { useReportsAccess } from "./reportsAccess";
import { normalizeStandardView } from "./saved/savedView";
import { SavedViewProvider } from "./saved/SavedViewContext";
import { NoAccess } from "./ui/ReportUi";

type ReportInfo = {
  path: string;
  name: string;
  description: string;
  icon: IconType;
  billing?: boolean;
  /** Added 2026-11-05: "New" tag on the card. */
  isNew?: boolean;
  element: ReactNode;
};

const WORK: ReportInfo[] = [
  {
    path: "workload",
    name: "Team workload",
    description:
      "Open work for each team member, by status, with what's overdue.",
    icon: LuUsers,
    element: <WorkloadReport />,
  },
  {
    path: "deadlines",
    name: "Deadlines",
    description:
      "Work that's overdue, due this week, or due in the next 30 days.",
    icon: LuCalendar,
    element: <DeadlinesReport />,
  },
  {
    path: "completed",
    name: "Completed work",
    description:
      "How much work got done, how much was on time, and how long it took.",
    icon: LuCheck,
    element: <CompletedReport />,
  },
  {
    path: "time-in-status",
    name: "Time in status",
    description:
      "How long work has been sitting in its current status, to spot bottlenecks.",
    icon: LuHourglass,
    element: <TimeInStatusReport />,
  },
  {
    path: "waiting",
    name: "Waiting on client",
    description:
      "Work that can't move until the client replies or sends something.",
    icon: LuMessageSquare,
    element: <WaitingReport />,
  },
  {
    path: "budget",
    name: "Budget vs actual hours",
    description:
      "Hours budgeted compared with hours tracked, and what's over budget.",
    icon: LuActivity,
    element: <BudgetReport />,
  },
  {
    path: "forecast",
    name: "Busy season forecast",
    description:
      "Work coming due in the next months, including recurring copies not created yet.",
    icon: LuCalendarClock,
    isNew: true,
    element: <ForecastReport />,
  },
  {
    path: "throughput",
    name: "Work started vs. finished",
    description: "Is the backlog growing or shrinking? Week by week.",
    icon: LuArrowLeftRight,
    isNew: true,
    element: <ThroughputReport />,
  },
];

const TIME: ReportInfo[] = [
  {
    path: "time",
    name: "Time report",
    description:
      "Hours by team member, client and work type, billed and not billed yet.",
    icon: LuTimer,
    element: <TimeReport />,
  },
  {
    path: "utilization",
    name: "Team utilization",
    description: "Billable share of each person's time against a target.",
    icon: LuGauge,
    isNew: true,
    element: <UtilizationReport />,
  },
];

const BILLING: ReportInfo[] = [
  {
    path: "unbilled",
    name: "Unbilled time (WIP)",
    description: "Time that hasn't been put on an invoice yet, by client.",
    icon: LuReceipt,
    billing: true,
    element: <UnbilledReport />,
  },
  {
    path: "aging",
    name: "Unpaid invoices by age",
    description:
      "What clients owe, split into 0–30, 31–60, 61–90 and 90+ days.",
    icon: LuAlertTriangle,
    billing: true,
    element: <AgingReport />,
  },
  {
    path: "revenue",
    name: "Revenue",
    description: "Revenue by client, service and team member, month by month.",
    icon: LuDollarSign,
    billing: true,
    element: <RevenueReport />,
  },
  {
    path: "profitability",
    name: "Client profitability",
    description: "Value of time worked vs. billed and paid, per client.",
    icon: LuPieChart,
    billing: true,
    isNew: true,
    element: <ProfitabilityReport />,
  },
  {
    path: "payments",
    name: "Payments received",
    description: "Money that came in, by month, client and payment method.",
    icon: LuBanknote,
    billing: true,
    isNew: true,
    element: <PaymentsReport />,
  },
];

const CLIENTS: ReportInfo[] = [
  {
    path: "client-requests",
    name: "Client requests",
    description: "Who still owes documents, and how many reminders went out.",
    icon: LuClipboardList,
    isNew: true,
    element: <ClientRequestsReport />,
  },
  {
    path: "by-owner",
    name: "Clients by owner / manager",
    description: "Each person's clients, overdue work and balances.",
    icon: LuUserCheck,
    isNew: true,
    element: <ByOwnerReport />,
  },
  {
    path: "quiet-clients",
    name: "Quiet clients",
    description: "No work, time or invoices for 3, 6 or 12 months.",
    icon: LuMoon,
    isNew: true,
    element: <QuietClientsReport />,
  },
  {
    path: "portal",
    name: "Client portal and signatures",
    description: "Portal use and signatures still waiting.",
    icon: LuPenTool,
    isNew: true,
    element: <PortalReport />,
  },
];

const ALL = [...WORK, ...TIME, ...BILLING, ...CLIENTS];

const ReportCard: FC<{ report: ReportInfo; locked: boolean }> = ({
  report,
  locked,
}) => {
  const Icon = report.icon;
  return (
    <Link
      to={`/reports/${report.path}`}
      className={classNames(
        "flex min-h-[150px] flex-col gap-2 rounded-[14px] bg-white px-5 py-[18px] text-[#1F2433] no-underline hover:border-[#6B4EFF] hover:shadow-[0_6px_18px_rgba(31,36,51,0.08)]",
        report.isNew
          ? "border-2 border-[#6B4EFF] px-[19px] py-[17px]"
          : "border border-[#EDEFF3]",
      )}>
      <span
        className={
          "flex h-9 w-9 items-center justify-center rounded-[10px] bg-[#F0EDFF] text-[#6B4EFF]"
        }>
        <Icon size={18} strokeWidth={2.1} />
      </span>
      <span className={"flex items-center justify-between gap-2"}>
        <span className={"text-[15.5px] font-extrabold"}>{report.name}</span>
        {report.isNew && (
          <span
            className={
              "shrink-0 rounded-full bg-[#F0EDFF] px-2 py-0.5 text-[11px] font-bold text-[#4B32D6]"
            }>
            New
          </span>
        )}
      </span>
      <span className={"text-[13px] leading-[1.45] text-[#5B6472]"}>
        {report.description}
      </span>
      <span className={"mt-auto flex justify-between text-[12.5px] font-bold"}>
        {locked ? (
          <span className={"inline-flex items-center gap-1 text-[#5B6472]"}>
            <LuLock size={13} /> Needs billing access
          </span>
        ) : (
          <span />
        )}
        <span className={"text-[#6B4EFF]"}>Open →</span>
      </span>
    </Link>
  );
};

const Section: FC<{
  id: string;
  title: string;
  subtitle: string;
  reports: ReportInfo[];
  billing: boolean;
}> = ({ id, title, subtitle, reports, billing }) => (
  <section aria-labelledby={id} className={"flex flex-col gap-3"}>
    <div className={"flex flex-wrap items-baseline gap-2.5"}>
      <h2 id={id} className={"text-lg font-extrabold"}>
        {title}
      </h2>
      <span className={"text-[13px] text-[#5B6472]"}>{subtitle}</span>
    </div>
    <div
      className={"grid gap-3.5"}
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))" }}>
      {reports.map((report) => (
        <ReportCard
          key={report.path}
          report={report}
          locked={!!report.billing && !billing}
        />
      ))}
    </div>
  </section>
);

const ReportsHome: FC = () => {
  const access = useReportsAccess();
  const { isOn: aiOn } = useAiStatus();
  return (
    <div
      className={
        "min-h-full bg-[#F6F7FB] px-4 pb-14 pt-8 font-Manrope text-[#1F2433] sm:px-8"
      }>
      <div className={"mx-auto flex max-w-[1240px] flex-col gap-7"}>
        <div>
          <h1 className={"text-[28px] font-extrabold leading-tight"}>
            Reports
          </h1>
          <p className={"mt-1.5 max-w-[680px] text-sm text-[#5B6472]"}>
            See how your firm&apos;s work, time and billing are going. Every
            report can be filtered and exported to Excel.
            {!access.firmWide && " You see your own work and time."}
          </p>
        </div>
        {aiOn && <CreateReportBox />}
        <MyReports />
        <Section
          id={"reports-work"}
          title={"Work"}
          subtitle={"Who is doing what, and what needs attention"}
          reports={WORK}
          billing={access.billing}
        />
        <Section
          id={"reports-time"}
          title={"Time"}
          subtitle={"Where the team's hours go"}
          reports={TIME}
          billing={access.billing}
        />
        <Section
          id={"reports-billing"}
          title={"Billing"}
          subtitle={"What's billed, owed and earned"}
          reports={BILLING}
          billing={access.billing}
        />
        <Section
          id={"reports-clients"}
          title={"Clients"}
          subtitle={"Who owes what, who's quiet, who uses the portal"}
          reports={CLIENTS}
          billing={access.billing}
        />
      </div>
    </div>
  );
};

const BillingGate: FC<{ children: ReactNode }> = ({ children }) => {
  const access = useReportsAccess();
  if (access.billing) return <>{children}</>;
  return (
    <div
      className={
        "min-h-full bg-[#F6F7FB] px-4 py-10 font-Manrope text-[#1F2433] sm:px-8"
      }>
      <div className={"mx-auto max-w-[1240px]"}>
        <Link
          to={"/reports"}
          className={"text-[13px] font-bold text-[#6B4EFF]"}>
          ← All reports
        </Link>
        <div className={"mt-4"}>
          <NoAccess
            text={
              "Billing reports are only for Admins and people with View Billing permission. Ask a firm admin if you need them."
            }
          />
        </div>
      </div>
    </div>
  );
};

export const ReportsView: FC = () => {
  const dispatch = useAppDispatch();
  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;
  const access = useReportsAccess();

  useEffect(() => {
    if (!userInfoByEmail?.id) return;
    dispatch(
      getUser({
        userId: `${userInfoByEmail.id}`,
        orgId: `${userInfoByEmail.org?.id}`,
      }),
    );
  }, [dispatch, userInfoByEmail?.id, userInfoByEmail?.org?.id]);

  if (access.isLoading) return <LottieLoading />;
  if (!access.canOpen) return <ReportsNoAccess />;

  return <ReportsRoutes />;
};

/** Shown instead of Reports to people without "View Reports" (Roadmap 1862). */
export const ReportsNoAccess: FC = () => (
  <div
    className={
      "min-h-full bg-[#F6F7FB] px-4 py-10 font-Manrope text-[#1F2433] sm:px-8"
    }>
    <div className={"mx-auto max-w-[1240px]"}>
      <h1 className={"mb-4 text-[28px] font-extrabold leading-tight"}>
        Reports
      </h1>
      <NoAccess
        text={
          "Reports are only for Admins and people with the View Reports permission. Ask a firm admin to turn it on for you in Settings > Team."
        }
      />
    </div>
  </div>
);

/** A standard report saved with its filters ("Save as my report", `/reports/saved/:id`). */
const SavedStandardReport: FC = () => {
  const { id = "" } = useParams();
  const orgId = getLinkedFirm()?.orgId || "";
  const saved = useGetCustomReportQuery({ id, orgId }, { skip: !id || !orgId });
  const view = useMemo(
    () => normalizeStandardView(saved.data?.spec),
    [saved.data?.spec],
  );
  if (saved.isLoading) return <LottieLoading />;
  if (saved.data && !view)
    return <Navigate to={`/reports/custom/${id}`} replace />;
  const report = view && ALL.find((r) => r.path === view.report);
  if (!saved.data || !view || !report)
    return (
      <div
        className={
          "min-h-full bg-[#F6F7FB] px-4 py-10 font-Manrope text-[#1F2433] sm:px-8"
        }>
        <div className={"mx-auto max-w-[1240px]"}>
          <Link
            to={"/reports"}
            className={"text-[13px] font-bold text-[#6B4EFF]"}>
            ← All reports
          </Link>
          <div className={"mt-4"}>
            <NoAccess
              text={
                "This report doesn't exist any more, or it isn't shared with you."
              }
            />
          </div>
        </div>
      </div>
    );
  const page = (
    <SavedViewProvider
      key={id}
      report={report.path}
      initial={view}
      saved={saved.data}>
      {report.element}
    </SavedViewProvider>
  );
  return report.billing ? <BillingGate>{page}</BillingGate> : page;
};

/** The Reports home page and the 19 reports (the user is already loaded). */
export const ReportsRoutes: FC = () => (
  <Routes>
    <Route index element={<ReportsHome />} />
    <Route path={"custom/new"} element={<CustomReportPage />} />
    <Route path={"custom/:id"} element={<CustomReportPage />} />
    <Route path={"saved/:id"} element={<SavedStandardReport />} />
    {ALL.map((report) => (
      <Route
        key={report.path}
        path={report.path}
        element={
          <SavedViewProvider key={report.path} report={report.path}>
            {report.billing ? (
              <BillingGate>{report.element}</BillingGate>
            ) : (
              report.element
            )}
          </SavedViewProvider>
        }
      />
    ))}
    <Route path={"*"} element={<Navigate to={"/reports"} replace />} />
  </Routes>
);
